import React, { useState, useEffect, useRef } from "react";
import { useCompletedTimers } from "../../context/CompletedTimersContext";
import CustomerNameLink from "./CustomerNameLink";
import "./SessionTimer.css";

const SessionTimer = ({
  booking,
  onDurationChange,
  onComplete,
  defaultDuration = null,
}) => {
  // Calculate end time based on startedAt and duration
  const calculateEndTime = () => {
    // Only create timers for sessions that are in-progress and have a startedAt timestamp
    if (
      !booking ||
      !booking.startedAt ||
      booking.status.toLowerCase() !== "in-progress"
    )
      return null;

    const startTime = new Date(booking.startedAt);
    const durationMs = (booking.duration || defaultDuration || 50) * 60 * 1000;
    return new Date(startTime.getTime() + durationMs);
  };

  const [endTime, setEndTime] = useState(calculateEndTime());
  const [timeLeft, setTimeLeft] = useState({ minutes: 0, seconds: 0 });
  const [isRunning, setIsRunning] = useState(true);
  const [isCompleted, setIsCompleted] = useState(false);
  const [showAdjustControls, setShowAdjustControls] = useState(false);
  const [adjustmentValue, setAdjustmentValue] = useState(5); // Default adjustment of 5 minutes
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  // Access the completed timers context
  const { addCompletedTimer, removeCompletedTimer } = useCompletedTimers();

  // Initialize or update the timer when booking changes
  useEffect(() => {
    const newEndTime = calculateEndTime();
    setEndTime(newEndTime);

    // If we have a valid end time, start the timer
    if (newEndTime) {
      setIsRunning(true);
    }

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [booking]);

  // Timer logic
  useEffect(() => {
    if (!endTime || !isRunning) return;

    const updateTimeLeft = () => {
      const now = new Date();
      const diff = endTime - now;

      if (diff <= 0) {
        // Timer has ended
        setTimeLeft({ minutes: 0, seconds: 0 });
        setIsRunning(false);
        setIsCompleted(true);
        clearInterval(timerRef.current);

        // Play sound when timer ends
        if (audioRef.current) {
          audioRef.current
            .play()
            .catch((e) => console.error("Error playing audio:", e));
        }

        // Add to completed timers list
        addCompletedTimer(booking);

        return;
      }

      // Calculate minutes and seconds
      const minutes = Math.floor(diff / 60000);
      const seconds = Math.floor((diff % 60000) / 1000);
      setTimeLeft({ minutes, seconds });

      // Alert when 5 minutes or 1 minute remaining
      if (
        (minutes === 5 && seconds === 0) ||
        (minutes === 1 && seconds === 0)
      ) {
        if (audioRef.current) {
          audioRef.current
            .play()
            .catch((e) => console.error("Error playing audio:", e));
        }
      }
    };

    // Update immediately and then set interval
    updateTimeLeft();
    timerRef.current = setInterval(updateTimeLeft, 1000);

    return () => {
      if (timerRef.current) {
        clearInterval(timerRef.current);
      }
    };
  }, [endTime, isRunning, booking, onComplete]);

  // Handle pause/resume
  const toggleTimer = () => {
    setIsRunning(!isRunning);
  };

  // Handle duration adjustment
  const adjustDuration = (minutes) => {
    if (!endTime) return;

    const newEndTime = new Date(endTime.getTime() + minutes * 60 * 1000);
    setEndTime(newEndTime);

    // Calculate new total duration
    if (booking && booking.startedAt && onDurationChange) {
      const startTime = new Date(booking.startedAt);
      const newDurationMinutes = Math.round((newEndTime - startTime) / 60000);
      onDurationChange(booking._id, newDurationMinutes);
    }
  };

  // Get timer color based on time left
  const getTimerColor = () => {
    if (timeLeft.minutes <= 1) return "red";
    if (timeLeft.minutes <= 5) return "yellow";
    return "green";
  };

  // Format time as MM:SS
  const formatTime = (minutes, seconds) => {
    return `${minutes.toString().padStart(2, "0")}:${seconds
      .toString()
      .padStart(2, "0")}`;
  };

  // Get suite type and number for display
  const getSuiteInfo = () => {
    if (!booking || !booking.suiteAssignment) return "Unknown Suite";

    const { type, number } = booking.suiteAssignment;
    const suiteLabel =
      type === "sauna"
        ? "Sauna"
        : type === "massage"
          ? "Massage"
          : "Red Light";
    return `${suiteLabel} #${number}`;
  };

  // Get member name for display
  const getMemberName = () => {
    if (!booking || !booking.user) return "Unknown Member";

    const user = booking.user;
    if (typeof user === "object") {
      return user.firstName && user.lastName
        ? `${user.firstName} ${user.lastName}`
        : user.username || "Unknown Member";
    }

    return "Unknown Member";
  };

  // Get the appropriate CSS classes for the timer
  const getTimerClasses = () => {
    let classes = "session-timer";

    // Add color class
    classes += ` ${getTimerColor()}`;

    // Add completed class if timer is completed
    if (isCompleted) {
      classes += " completed";
    }

    return classes;
  };

  return (
    <div className={getTimerClasses()}>
      <audio ref={audioRef} src="/sounds/timer-alert.mp3" />

      <div className="timer-header">
        <div className="suite-info">{getSuiteInfo()}</div>
        <div className="member-name">
          <CustomerNameLink
            customerId={
              typeof booking?.user === "object" ? booking.user?._id : null
            }
          >
            {getMemberName()}
          </CustomerNameLink>
        </div>
      </div>

      <div className="timer-display">
        <div className="time-remaining">
          {formatTime(timeLeft.minutes, timeLeft.seconds)}
        </div>

        <div className="timer-controls">
          <button className="timer-btn" onClick={toggleTimer}>
            {isRunning ? "Pause" : "Resume"}
          </button>

          <button
            className="timer-btn adjust-btn"
            onClick={() => setShowAdjustControls(!showAdjustControls)}
          >
            Adjust
          </button>

          {showAdjustControls && (
            <div className="adjustment-controls">
              <button
                className="adjust-btn decrease"
                onClick={() => adjustDuration(-adjustmentValue)}
              >
                -{adjustmentValue}m
              </button>

              <select
                value={adjustmentValue}
                onChange={(e) => setAdjustmentValue(Number(e.target.value))}
                className="adjustment-select"
              >
                <option value={1}>1m</option>
                <option value={5}>5m</option>
                <option value={10}>10m</option>
                <option value={15}>15m</option>
              </select>

              <button
                className="adjust-btn increase"
                onClick={() => adjustDuration(adjustmentValue)}
              >
                +{adjustmentValue}m
              </button>
            </div>
          )}

          <button
            className="timer-btn complete-btn"
            onClick={() => {
              if (isCompleted) {
                removeCompletedTimer(booking._id);
                setIsCompleted(false);
              }
              if (onComplete) {
                onComplete(booking._id);
              }
            }}
          >
            Complete
          </button>
        </div>
      </div>

      <div className="timer-footer">
        <div className="session-type">
          {booking?.displayLabel ||
            (booking?.sessionType === "infrared"
              ? "Infrared Sauna Session"
              : booking?.sessionType === "redlight"
                ? "Red Light Session"
                : "Wellness Session")}
        </div>
        <div className="start-time">
          Started:{" "}
          {booking?.startedAt
            ? new Date(booking.startedAt).toLocaleTimeString()
            : "N/A"}
        </div>
      </div>
    </div>
  );
};

export default SessionTimer;
